import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPills,
  FaUserNurse,
  FaBalanceScale,
  FaWalking,
  FaBriefcase,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import collegeService from "../services/collegeService";
import logo from "../assets/images/cigoi.png"

// Fixed discipline shell — icon + display order are the only things kept
// hardcoded now. The actual college lists come from GET /colleges/public
// (added alongside the College model), grouped in below real data.
const DISCIPLINE_SHELL = [
  { key: "Pharmacy", label: "Pharmacy", icon: FaPills },
  { key: "Nursing", label: "Nursing", icon: FaUserNurse },
  { key: "Law", label: "Law", icon: FaBalanceScale },
  { key: "Physiotherapy", label: "Physiotherapy", icon: FaWalking },
  { key: "Business Administration", label: "Business Administration", icon: FaBriefcase },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null); // discipline key, or null = grid view
  const [collegesByDiscipline, setCollegesByDiscipline] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    collegeService
      .getPublic()
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        const grouped = {};
        list.forEach((c) => {
          if (!grouped[c.discipline]) grouped[c.discipline] = [];
          grouped[c.discipline].push(c);
        });
        setCollegesByDiscipline(grouped);
      })
      .catch(() => setCollegesByDiscipline({})) // fail quietly — landing page still renders, just shows "coming soon" everywhere
      .finally(() => setLoading(false));
  }, []);

  const disciplines = DISCIPLINE_SHELL.map((d) => ({
    ...d,
    colleges: collegesByDiscipline[d.key] || [],
  }));
  const discipline = disciplines.find((d) => d.key === selected) || null;

  // All colleges log in through the same shared /login — there's no
  // separate domain per college (see the CIGOI multi-tenancy design:
  // one shared cluster/app, scoped by the logged-in user's linkedCollege).
  // The college `code` is passed through only as a UI hint, not required
  // for login to function.
  const handleEnter = (college) => navigate(`/login?college=${college.code}`);


  return (
    <div className="cigoi-landing">
      <style>{CSS}</style>

      <div className="cigoi-bg" aria-hidden="true" />

      <header className="cigoi-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="cigoi-mark"></div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={() => navigate("/staff/leave-apply")}
            style={{
              background: "rgba(27,42,74,0.06)",
              border: "1px solid rgba(27,42,74,0.15)",
              padding: "5px 12px",
              borderRadius: 99,
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "var(--ink)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            Staff Leave Portal &rarr;
          </button>
          <button
            type="button"
            onClick={() => navigate("/superadmin/login")}
            style={{
              background: "#0f172a",
              border: "1px solid rgba(176,141,69,0.4)",
              padding: "5px 12px",
              borderRadius: 99,
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#f7d583",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)"
            }}
          >
            Super Admin &rarr;
          </button>
        </div>
      </header>

      <main className="cigoi-main">
        {!discipline ? (
          <section key="grid" className="cigoi-view cigoi-grid-view">
            {/* <p className="cigoi-eyebrow">Prospectus &middot; 2026&ndash;27</p> */}
            <img src={logo} alt="Central India Group of Institutions" style={{ width: "200px", height: "200px" }} />
            <h2 className="cigoi-title">Central India Group of Institutions</h2>
            <p className="cigoi-sub">
              Select a discipline to find your college and continue to its portal.
            </p>

            <div className="cigoi-seal-grid">
              {disciplines.map((d, i) => {
                const Icon = d.icon;
                return (
                  <button
                    key={d.key}
                    className="cigoi-seal-card"
                    style={{ "--i": i }}
                    onClick={() => setSelected(d.key)}
                  >
                    <span className="cigoi-seal">
                      <Icon size={52} />
                    </span>
                    <span className="cigoi-seal-label">{d.label}</span>
                    <span className="cigoi-seal-count">
                      {d.colleges.length > 0
                        ? `${d.colleges.length} college${d.colleges.length > 1 ? "s" : ""}`
                        : "coming soon"}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ) : (
          <section key="ledger" className="cigoi-view cigoi-ledger-view">
            <button className="cigoi-back" onClick={() => setSelected(null)}>
              <FaArrowLeft size={12} /> All Disciplines
            </button>

            <p className="cigoi-eyebrow">{discipline.label}</p>
            <h1 className="cigoi-title cigoi-title-sm">Colleges under this discipline</h1>

            {discipline.colleges.length === 0 ? (
              <div className="cigoi-empty">
                No colleges are listed under {discipline.label} yet. Check back soon.
              </div>
            ) : (
              <ol className="cigoi-ledger">
                {discipline.colleges.map((c, i) => (
                  <li key={c._id} className="cigoi-ledger-row" style={{ "--i": i }}>
                    <span className="cigoi-ledger-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="cigoi-ledger-name">{c.name}</span>
                    <button className="cigoi-ledger-enter" onClick={() => handleEnter(c)}>
                      Enter <FaArrowRight size={11} />
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </section>
        )}
      </main>

      <footer className="cigoi-footer">
        <div>Central India Group of Institutions &mdash; Pharmacy &middot; Nursing &middot; Law &middot; Physiotherapy &middot; Business Administration</div>
        <div style={{ marginTop: 6, display: "flex", justifyContent: "center", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => navigate("/staff/leave-apply")}
            style={{
              background: "none",
              border: "none",
              color: "var(--brass, #B08D45)",
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Non-Teaching Staff Leave Application Form
          </button>
          <span style={{ color: "#94a3b8" }}>&middot;</span>
          <button
            type="button"
            onClick={() => navigate("/superadmin/login")}
            style={{
              background: "none",
              border: "none",
              color: "#94a3b8",
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Super Admin Portal
          </button>
        </div>
      </footer>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');

.cigoi-landing {
  --ink: #1B2A4A;
  --ink-soft: #2C3D61;
  --parchment: #F6F1E4;
  --parchment-2: #EFE8D6;
  --brass: #B08D45;
  --brass-light: #D4B26A;
  --slate: #5B6B82;
  --white: #FFFFFF;

  position: relative;
  min-height: 100vh;
  background: var(--parchment);
  color: var(--ink);
  font-family: 'Inter', system-ui, sans-serif;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.cigoi-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(600px 400px at 12% -10%, rgba(176,141,69,0.14), transparent 60%),
    radial-gradient(700px 500px at 110% 10%, rgba(27,42,74,0.08), transparent 60%);
}

.cigoi-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  padding: 1.75rem 2rem 0;
}
.cigoi-mark {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 1.05rem;
  letter-spacing: 0.14em;
  color: var(--ink);
}
.cigoi-mark-full {
  font-size: 0.72rem;
  color: var(--slate);
  letter-spacing: 0.02em;
}

.cigoi-main {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem 1.0rem;
}

.cigoi-view {
  width: 100%;
  max-width: 780px;
  text-align: center;
  animation: cigoiRise 0.5s cubic-bezier(.2,.7,.3,1) both;
}
@keyframes cigoiRise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.cigoi-eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--brass);
  margin: 0 0 0.6rem;
}

.cigoi-title {
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 1.12;
  margin: 0 0 0.65rem;
  color: var(--ink);
}
.cigoi-title-sm {
  font-size: clamp(1.5rem, 3vw, 2rem);
}

.cigoi-sub {
  font-size: 0.98rem;
  color: var(--slate);
  margin: 0 auto 1.0rem;
  max-width: 34rem;
}

/* ── Discipline seal grid ─────────────────────────────────────────────── */
.cigoi-seal-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.1rem;
  margin-top: 1rem;
}
@media (max-width: 640px) {
  .cigoi-seal-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.cigoi-seal-card {
  appearance: none;
  border: 1px solid rgba(27,42,74,0.12);
  background: var(--white);
  border-radius: 14px;
  padding: 1.6rem 1rem 1.3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
  cursor: pointer;
  font-family: inherit;
  color: var(--ink);
  box-shadow: 0 1px 2px rgba(27,42,74,0.05);
  transition: transform 0.22s cubic-bezier(.2,.7,.3,1), box-shadow 0.22s ease, border-color 0.22s ease;
  animation: cigoiRise 0.5s cubic-bezier(.2,.7,.3,1) both;
  animation-delay: calc(var(--i) * 60ms);
}
.cigoi-seal-card:hover,
.cigoi-seal-card:focus-visible {
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(27,42,74,0.12);
  border-color: rgba(176,141,69,0.4);
}
.cigoi-seal-card:focus-visible {
  outline: 2px solid var(--brass);
  outline-offset: 2px;
}

.cigoi-seal {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  background: linear-gradient(155deg, var(--brass-light), var(--brass));
  box-shadow: inset 0 0 0 3px rgba(255,255,255,0.35), 0 3px 8px rgba(176,141,69,0.35);
  transition: transform 0.35s cubic-bezier(.2,.7,.3,1);
}
.cigoi-seal-card:hover .cigoi-seal {
  transform: rotate(-8deg) scale(1.05);
}

.cigoi-seal-label {
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: 1rem;
}
.cigoi-seal-count {
  font-size: 0.7rem;
  color: var(--slate);
  letter-spacing: 0.03em;
}

/* ── College ledger ───────────────────────────────────────────────────── */
.cigoi-back {
  appearance: none;
  border: none;
  background: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--slate);
  cursor: pointer;
  margin-bottom: 1.5rem;
  padding: 0.3rem 0;
}
.cigoi-back:hover { color: var(--ink); }
.cigoi-back:focus-visible { outline: 2px solid var(--brass); outline-offset: 3px; border-radius: 4px; }

.cigoi-ledger {
  list-style: none;
  margin: 1.75rem 0 0;
  padding: 0;
  text-align: left;
  border-top: 1px solid rgba(27,42,74,0.14);
}
.cigoi-ledger-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 0.25rem;
  border-bottom: 1px solid rgba(27,42,74,0.14);
  animation: cigoiRise 0.45s cubic-bezier(.2,.7,.3,1) both;
  animation-delay: calc(var(--i) * 70ms);
}
.cigoi-ledger-num {
  font-family: 'Fraunces', serif;
  font-size: 0.85rem;
  color: var(--brass);
  width: 1.6rem;
  flex-shrink: 0;
}
.cigoi-ledger-name {
  font-family: 'Fraunces', serif;
  font-size: 1.08rem;
  flex: 1;
  color: var(--ink);
}
.cigoi-ledger-enter {
  appearance: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--white);
  background: var(--ink);
  padding: 0.5rem 0.95rem;
  border-radius: 99px;
  text-decoration: none;
  flex-shrink: 0;
  transition: background 0.2s ease, transform 0.2s ease;
}
.cigoi-ledger-enter:hover { background: var(--ink-soft); transform: translateX(2px); }
.cigoi-ledger-enter:focus-visible { outline: 2px solid var(--brass); outline-offset: 2px; }

.cigoi-ledger-soon {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--slate);
  background: var(--parchment-2);
  padding: 0.42rem 0.85rem;
  border-radius: 99px;
  flex-shrink: 0;
}

.cigoi-empty {
  margin-top: 2rem;
  padding: 2rem 1.5rem;
  border: 1px dashed rgba(27,42,74,0.25);
  border-radius: 12px;
  color: var(--slate);
  font-size: 0.92rem;
}

.cigoi-footer {
  position: relative;
  z-index: 1;
  text-align: center;
  font-size: 0.72rem;
  color: var(--slate);
  padding: 0.75rem 1.5rem 1.25rem;
  letter-spacing: 0.01em;
}

@media (prefers-reduced-motion: reduce) {
  .cigoi-view, .cigoi-seal-card, .cigoi-ledger-row, .cigoi-seal-card:hover .cigoi-seal {
    animation: none !important;
    transition: none !important;
    transform: none !important;
  }
}
`;